/**
 * Fantasy Console 0 - SFX Type Definitions
 *
 * PICO-8-style sound effect system:
 * - 64 SFX slots (0-63)
 * - 32 notes per SFX
 * - 8 waveforms, 8 effects
 * - 4 simultaneous audio channels
 */

/**
 * A single note within an SFX
 */
export interface SFXNote {
  /** Pitch value 0-63 (C-1 to D#-6) */
  pitch: number
  /** Waveform type 0-7 */
  waveform: number
  /** Volume 0-7 (0=silent, 7=max) */
  volume: number
  /** Effect type 0-7 */
  effect: number
}

/**
 * A sound effect containing 32 notes and playback settings
 */
export interface SFX {
  /** Ticks per note, 1-255 (higher = slower) */
  speed: number
  /** Loop start position 0-31 */
  loop_start: number
  /** Loop end position 0-31 */
  loop_end: number
  /** Always 32 notes */
  notes: SFXNote[]
}

/**
 * SFX system constants
 */
export const SFX_COUNT = 64
export const NOTES_PER_SFX = 32
export const SFX_CHANNELS = 4
export const BYTES_PER_NOTE = 4
export const SFX_HEADER_BYTES = 3
export const BYTES_PER_SFX = SFX_HEADER_BYTES + NOTES_PER_SFX * BYTES_PER_NOTE // 131
export const SFX_BANK_BYTES = SFX_COUNT * BYTES_PER_SFX // 8384

/**
 * Waveform types
 */
export const WAVEFORM = {
  SINE: 0,
  TRIANGLE: 1,
  SAWTOOTH: 2,
  SQUARE: 3,
  PULSE_25: 4,
  PULSE_12: 5,
  NOISE: 6,
  SAWTOOTH_ROUNDED: 7,
} as const

/**
 * Effect types
 */
export const EFFECT = {
  NONE: 0,
  SLIDE: 1,
  VIBRATO: 2,
  DROP: 3,
  FADE_IN: 4,
  FADE_OUT: 5,
  ARPEGGIO_FAST: 6,
  ARPEGGIO_SLOW: 7,
} as const
